import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import PhoneInput, {
  isPossiblePhoneNumber,
  getCountryCallingCode
} from 'react-phone-number-input'

const LocalTextInput = React.forwardRef(({ value, onChange, ...rest }, ref) => {
  return <StyledInput {...rest} ref={ref} value={value} onChange={onChange} />
})

const StyledInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  color: ${({ theme }) => theme.colors.black.hex()};
  background-color: white;
  border: 1px solid ${({ theme }) => theme.colors.ghost.hex()};
  border-radius: 2px;
  font-size: 0.875rem;
  outline: none;
  vertical-align: middle;

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 2px 10px 0 rgba(19, 162, 191, 0.25);
  }
`

const EcontainersPhoneInput = ({
  value,
  onChange,
  label,
  required,
  name,
  disabled,
  validate,
  pristine,
  helperText,
  placeholder,
  optional,
  defaultCountry
}) => {
  const [error, setError] = useState('')
  const [isValid, setIsValid] = useState(false)

  useEffect(() => {
    if (!value) {
      setIsValid(false)
    } else {
      setIsValid(isPossiblePhoneNumber(value))
    }
  }, [value])

  const handlePhoneChange = phoneValue => {
    if (!phoneValue || isPossiblePhoneNumber(phoneValue)) {
      setError('')
    } else {
      setError('Invalid phone number format')
    }
    onChange(name, phoneValue)
  }

  return (
    <Container>
      <Label required={required}>
        {label}
        {optional && (
          <Optional>
            <FormattedMessage id='forms.optional' />
          </Optional>
        )}
      </Label>
      <InputContainer>
        <StyledPhoneInput
          placeholder={placeholder}
          value={value}
          onChange={handlePhoneChange}
          disabled={disabled}
          defaultCountry={defaultCountry} // Przekaż prop do StyledPhoneInput
          error={error || helperText}
          international={false}
          countrySelectComponent={CountrySelect}
          inputComponent={CustomPhoneInput}
        />
      </InputContainer>
    </Container>
  )
}

// Dodaj domyślną wartość dla defaultCountry
EcontainersPhoneInput.defaultProps = {
  defaultCountry: 'PL'
}

// Zmodyfikuj CustomPhoneInput, aby używać LocalTextInput
const CustomPhoneInput = React.forwardRef(
  ({ value, onChange, ...rest }, ref) => {
    return (
      <LocalTextInput
        {...rest}
        value={value}
        onChange={e => onChange(e.target.value)}
        ref={ref}
      />
    )
  }
)

const CountrySelect = ({ value, onChange, labels, ...rest }) => {
  const countriesList = [
    'PL',
    'DE',
    'UA',
    'BY',
    'CZ',
    'IT',
    'FR',
    'LT',
    'LV',
    'EE'
  ]

  return (
    <Select
      {...rest}
      value={value || ''}
      onChange={event => onChange(event.target.value || undefined)}
    >
      {countriesList.map(country => (
        <option key={country} value={country}>
          {labels ? labels[country] : country} +{getCountryCallingCode(country)}
        </option>
      ))}
    </Select>
  )
}

const Select = styled.select`
  padding: 8px;
  border: 1px solid ${({ theme }) => theme.colors.ghost.hex()};
  border-radius: 2px;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.black.hex()};
  background-color: white;
  outline: none;
  margin-right: 8px;
  height: 40px; /* Ensure the height matches the input field */
  vertical-align: middle;

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`

const Label = styled.span`
  display: flex;
  width: 100%;
  color: ${({ theme }) => theme.colors.black.hex()};
  font-weight: 600;
  font-size: 0.75rem;
  margin-bottom: 0.1rem;
  margin-left: 0.2rem;
  ${({ required }) =>
    required &&
    `
    &:after {
      content: '*';
      color: red;
      font-size: 0.75rem;
    }
  `}
`

const Optional = styled.span`
  font-size: 0.75rem;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.black.alpha(0.7).hex()};
  margin-left: 0.2rem;
`

const InputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 4px;
  margin-bottom: 1rem;
`

const StyledPhoneInput = styled(PhoneInput)`
  display: flex;
  width: 100%;
  align-items: center;

  & > div {
    display: flex;
    align-items: center;
  }
`

export { EcontainersPhoneInput }
